import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, MessageCircle, ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-white fill-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">TicketDesk</h1>
                <p className="text-xs text-gray-500">Customer Support Platform</p>
              </div>
            </Link>

            <Link
              to="/"
              className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
            <Shield className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-gray-600">Last updated: {currentDate}</p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 md:p-12 space-y-8">

          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Welcome to TicketDesk ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our customer support platform and related services.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By using TicketDesk, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">1.1 Information You Provide</h3>
            <p className="text-gray-700 leading-relaxed mb-3">We collect information that you voluntarily provide to us, including:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Account Information:</strong> Name, email address, password, and company details when you create an account</li>
              <li><strong>Profile Information:</strong> Additional information you choose to add to your profile</li>
              <li><strong>Support Tickets:</strong> Messages, attachments, and communication exchanged through our platform</li>
              <li><strong>Customer Data:</strong> Information about your customers including names, email addresses, and support interactions</li>
              <li><strong>Payment Information:</strong> Billing details and payment method information (processed securely through Stripe)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">1.2 Information Collected Automatically</h3>
            <p className="text-gray-700 leading-relaxed mb-3">When you use our services, we automatically collect certain information:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Usage Data:</strong> Pages viewed, features used, time spent, and interaction patterns</li>
              <li><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers</li>
              <li><strong>Log Data:</strong> Access times, error logs, and system activity</li>
              <li><strong>Cookies:</strong> Authentication tokens and preferences (see our Cookie Policy below)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">1.3 Information from Third Parties</h3>
            <p className="text-gray-700 leading-relaxed mb-3">We may receive information from third-party services you connect to TicketDesk:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Gmail Integration:</strong> Email messages, sender information, and email metadata</li>
              <li><strong>Facebook Messenger:</strong> Messages, user IDs, and page information</li>
              <li><strong>Review Platforms:</strong> Reviews, ratings, and customer feedback from Trustpilot, G2, and Capterra</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-3">We use the information we collect for the following purposes:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Service Delivery:</strong> To provide, maintain, and improve our customer support platform</li>
              <li><strong>Communication:</strong> To send service updates, security alerts, and support messages</li>
              <li><strong>Customer Support:</strong> To respond to your questions and provide technical assistance</li>
              <li><strong>Analytics:</strong> To understand usage patterns and improve our services</li>
              <li><strong>Security:</strong> To detect, prevent, and address fraud, security issues, and technical problems</li>
              <li><strong>Compliance:</strong> To comply with legal obligations and enforce our terms of service</li>
              <li><strong>Marketing:</strong> To send promotional communications (with your consent, where required)</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Share Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-3">We do not sell your personal information. We may share your information in the following circumstances:</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.1 Service Providers</h3>
            <p className="text-gray-700 leading-relaxed mb-3">We share information with trusted third-party service providers who assist us in operating our platform:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Hosting Services:</strong> To store and process data</li>
              <li><strong>Email Services:</strong> Mailgun for transactional email delivery</li>
              <li><strong>Payment Processing:</strong> Stripe for secure payment handling</li>
              <li><strong>Analytics:</strong> To understand service usage and performance</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.2 Business Transfers</h3>
            <p className="text-gray-700 leading-relaxed">
              In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.3 Legal Requirements</h3>
            <p className="text-gray-700 leading-relaxed">
              We may disclose your information if required by law, court order, or government request, or to protect our rights, property, or safety.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.4 With Your Consent</h3>
            <p className="text-gray-700 leading-relaxed">
              We may share information with third parties when you give us explicit consent to do so.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Security</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We implement appropriate technical and organizational security measures to protect your information:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Encryption:</strong> Data is encrypted in transit using SSL/TLS and at rest</li>
              <li><strong>Access Controls:</strong> Strict access controls and authentication requirements</li>
              <li><strong>Monitoring:</strong> Regular security monitoring and vulnerability assessments</li>
              <li><strong>Backups:</strong> Regular automated backups to prevent data loss</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We retain your information for as long as necessary to provide our services and fulfill the purposes outlined in this policy:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Active Accounts:</strong> Data is retained while your account is active</li>
              <li><strong>Closed Accounts:</strong> Data is deleted or anonymized within 90 days of account closure</li>
              <li><strong>Legal Requirements:</strong> Some data may be retained longer to comply with legal obligations</li>
              <li><strong>Backup Systems:</strong> Data in backup systems is deleted according to our backup retention schedule</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights and Choices</h2>
            <p className="text-gray-700 leading-relaxed mb-3">Depending on your location, you may have the following rights:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Data Portability:</strong> Request transfer of your data to another service</li>
              <li><strong>Objection:</strong> Object to processing of your information</li>
              <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              To exercise these rights, please contact us at <a href="mailto:privacy@ticketdesk.app" className="text-purple-600 hover:text-purple-700 font-medium">privacy@ticketdesk.app</a>
            </p>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We use cookies and similar tracking technologies to enhance your experience:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Essential Cookies:</strong> Required for authentication and platform functionality</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our platform</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              You can control cookies through your browser settings. Note that disabling essential cookies may affect platform functionality.
            </p>
          </section>

          {/* Third-Party Integrations */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Third-Party Services and Integrations</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              TicketDesk integrates with third-party services to provide enhanced functionality:
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">8.1 Facebook Messenger Integration</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              When you connect Facebook Messenger to TicketDesk:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>We receive messages sent to your Facebook Page</li>
              <li>We access basic user information (name, profile picture, user ID) of people messaging your Page</li>
              <li>We send responses on behalf of your Page</li>
              <li>All data is handled in accordance with Facebook's Platform Terms and this Privacy Policy</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">8.2 Gmail Integration</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              When you connect Gmail to TicketDesk:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>We access and process incoming emails to create support tickets</li>
              <li>We send email responses through your Gmail account</li>
              <li>OAuth tokens are encrypted and stored securely</li>
              <li>You can revoke access at any time through your Google Account settings</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">8.3 Review Platform Integrations</h3>
            <p className="text-gray-700 leading-relaxed">
              When you connect review platforms (Trustpilot, G2, Capterra), we access publicly available reviews and ratings to help you monitor and respond to customer feedback.
            </p>
          </section>

          {/* International Data Transfers */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. International Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed">
              Your information may be transferred to and processed in countries other than your country of residence. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards, including standard contractual clauses approved by relevant authorities.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              TicketDesk is not intended for use by children under 16 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately at <a href="mailto:privacy@ticketdesk.app" className="text-purple-600 hover:text-purple-700 font-medium">privacy@ticketdesk.app</a>
            </p>
          </section>

          {/* GDPR Compliance */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. GDPR Compliance (European Users)</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR):
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Right to access your personal data</li>
              <li>Right to rectification of inaccurate data</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Right to lodge a complaint with a supervisory authority</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Our legal basis for processing your data includes: performance of contract, legitimate interests, and consent where required.
            </p>
          </section>

          {/* CCPA Compliance */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. CCPA Compliance (California Residents)</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              If you are a California resident, the California Consumer Privacy Act (CCPA) provides you with specific rights:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Right to know what personal information is collected</li>
              <li>Right to know whether personal information is sold or disclosed</li>
              <li>Right to say no to the sale of personal information</li>
              <li>Right to access your personal information</li>
              <li>Right to deletion of personal information</li>
              <li>Right to equal service and price</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>Note:</strong> We do not sell your personal information.
            </p>
          </section>

          {/* Changes to Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically to stay informed about how we protect your information.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <p className="text-gray-900 font-semibold mb-2">TicketDesk Privacy Team</p>
              <p className="text-gray-700">Email: <a href="mailto:privacy@ticketdesk.app" className="text-purple-600 hover:text-purple-700 font-medium">privacy@ticketdesk.app</a></p>
              <p className="text-gray-700">Support: <a href="mailto:support@ticketdesk.app" className="text-purple-600 hover:text-purple-700 font-medium">support@ticketdesk.app</a></p>
              <p className="text-gray-700 mt-3">Response Time: We aim to respond to all privacy inquiries within 72 hours</p>
            </div>
          </section>

          {/* Data Protection Officer */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Data Protection Officer</h2>
            <p className="text-gray-700 leading-relaxed">
              For GDPR-related inquiries, you can contact our Data Protection Officer at <a href="mailto:dpo@ticketdesk.app" className="text-purple-600 hover:text-purple-700 font-medium">dpo@ticketdesk.app</a>
            </p>
          </section>

          {/* Consent */}
          <section className="bg-gray-50 rounded-lg p-6 border-l-4 border-purple-600">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Your Consent</h3>
            <p className="text-gray-700 leading-relaxed">
              By using TicketDesk, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with this policy, please discontinue use of our services.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">
            This Privacy Policy is effective as of {currentDate} and applies to all users of TicketDesk.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium transition-colors"
          >
            <ArrowLeft size={18} />
            Return to Homepage
          </Link>
        </div>
      </div>

      {/* Footer Section */}
      <footer className="bg-white border-t border-gray-200 py-8 mt-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white fill-white" />
              </div>
              <span className="text-gray-600">© 2025 TicketDesk. All rights reserved.</span>
            </div>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-gray-600 hover:text-purple-600 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-600 hover:text-purple-600 transition-colors">Terms of Service</Link>
              <a href="mailto:support@ticketdesk.app" className="text-gray-600 hover:text-purple-600 transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
